import { createRoute, lazyRouteComponent, redirect } from '@tanstack/react-router';
import { authedRoute } from '../../app/routes-base';

export const backofficeRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: 'backoffice',
  component: lazyRouteComponent(() => import('./layout'), 'BackofficeLayout'),
});

const dashboardRoute = createRoute({
  getParentRoute: () => backofficeRoute,
  path: '/',
  component: lazyRouteComponent(() => import('./pages/dashboard-page'), 'DashboardPage'),
});

const floorsRoute = createRoute({
  getParentRoute: () => backofficeRoute,
  path: 'floors',
  component: lazyRouteComponent(() => import('./pages/floors-page'), 'FloorsPage'),
});

export type TableDetailTab = 'analytics' | 'settings';
const tableDetailRoute = createRoute({
  getParentRoute: () => backofficeRoute,
  path: 'floors/$tableId',
  validateSearch: (search: Record<string, unknown>): { tab: TableDetailTab } => ({
    tab: search.tab === 'settings' ? 'settings' : 'analytics',
  }),
  component: lazyRouteComponent(() => import('./pages/table-detail-page'), 'TableDetailPage'),
});

export type MenuTab = 'items' | 'categories' | 'modifiers';
const MENU_TABS: readonly MenuTab[] = ['items', 'categories', 'modifiers'];

export const menuRoute = createRoute({
  getParentRoute: () => backofficeRoute,
  path: 'menu',
  validateSearch: (search: Record<string, unknown>): { tab?: MenuTab } =>
    MENU_TABS.includes(search.tab as MenuTab) ? { tab: search.tab as MenuTab } : {},
  component: lazyRouteComponent(() => import('./pages/menu-page'), 'MenuPage'),
});

export type MenuDetailTab = 'analytics' | 'bom' | 'profitability';
export const menuDetailSearch = (search: Record<string, unknown>): { tab: MenuDetailTab } => ({
  tab: search.tab === 'bom' || search.tab === 'profitability' ? search.tab : 'analytics',
});
export const menuDetailRoute = createRoute({
  getParentRoute: () => backofficeRoute,
  path: 'menu/$itemId',
  validateSearch: menuDetailSearch,
  component: lazyRouteComponent(() => import('./pages/menu-item-detail'), 'MenuItemDetailPage'),
});

const qrCodesRoute = createRoute({
  getParentRoute: () => backofficeRoute,
  path: 'qr',
  component: lazyRouteComponent(() => import('./pages/qr-codes-page'), 'QrCodesPage'),
});

const promotionsRoute = createRoute({
  getParentRoute: () => backofficeRoute,
  path: 'promotions',
  component: lazyRouteComponent(() => import('./pages/promotions-page'), 'PromotionsPage'),
});

const pricingRoute = createRoute({
  getParentRoute: () => backofficeRoute,
  path: 'pricing',
  component: lazyRouteComponent(() => import('./pages/pricing-page'), 'PricingPage'),
});

const guestContactsRoute = createRoute({
  getParentRoute: () => backofficeRoute,
  path: 'guest-contacts',
  component: lazyRouteComponent(() => import('./pages/guest-contacts-page'), 'GuestContactsPage'),
});

const stationsRoute = createRoute({
  getParentRoute: () => backofficeRoute,
  path: 'stations',
  component: lazyRouteComponent(() => import('./pages/stations-page'), 'StationsPage'),
});

const staffRoute = createRoute({
  getParentRoute: () => backofficeRoute,
  path: 'staff',
  component: lazyRouteComponent(() => import('./pages/staff-page'), 'StaffPage'),
});

const auditRoute = createRoute({
  getParentRoute: () => backofficeRoute,
  path: 'audit',
  component: lazyRouteComponent(() => import('./pages/audit-page'), 'AuditPage'),
});

const reportsRoute = createRoute({
  getParentRoute: () => backofficeRoute,
  path: 'reports',
  component: lazyRouteComponent(() => import('./pages/reports-page'), 'ReportsPage'),
});

const branchesRoute = createRoute({
  getParentRoute: () => backofficeRoute,
  path: 'branches',
  component: lazyRouteComponent(() => import('./pages/branches-page'), 'BranchesPage'),
});

const settingsRoute = createRoute({
  getParentRoute: () => backofficeRoute,
  path: 'settings',
  component: lazyRouteComponent(() => import('./pages/settings-page'), 'SettingsPage'),
});

// Data & privacy: export, retention, cancellation (ADR-0034)
const dataPrivacyRoute = createRoute({
  getParentRoute: () => backofficeRoute,
  path: 'data',
  component: lazyRouteComponent(() => import('./pages/data-privacy-page'), 'DataPrivacyPage'),
});

// Subscription & invoices (ADR-0033)
const subscriptionRoute = createRoute({
  getParentRoute: () => backofficeRoute,
  path: 'langganan',
  component: lazyRouteComponent(() => import('./pages/subscription-page'), 'SubscriptionPage'),
});

const subscriptionInvoiceRoute = createRoute({
  getParentRoute: () => backofficeRoute,
  path: 'langganan/tagihan/$invoiceId',
  component: lazyRouteComponent(
    () => import('./pages/subscription-invoice-page'),
    'SubscriptionInvoicePage',
  ),
});

const supportAccessRoute = createRoute({
  getParentRoute: () => backofficeRoute,
  path: 'support-access',
  component: lazyRouteComponent(() => import('./pages/support-access-page'), 'SupportAccessPage'),
});

const brandingRoute = createRoute({
  getParentRoute: () => backofficeRoute,
  path: 'branding',
  component: lazyRouteComponent(() => import('./pages/branding-page'), 'BrandingPage'),
});

const approvalsRoute = createRoute({
  getParentRoute: () => backofficeRoute,
  path: 'approvals',
  validateSearch: (search: Record<string, unknown>): { tab?: 'inbox' | 'history' } =>
    search.tab === 'history' || search.tab === 'inbox' ? { tab: search.tab } : {},
  component: lazyRouteComponent(() => import('./pages/approvals-page'), 'ApprovalsPage'),
});

const approvalPoliciesRoute = createRoute({
  getParentRoute: () => backofficeRoute,
  path: 'approval-policies',
  component: lazyRouteComponent(
    () => import('./pages/approval-policies-page'),
    'ApprovalPoliciesPage',
  ),
});

const delegationsRoute = createRoute({
  getParentRoute: () => backofficeRoute,
  path: 'delegations',
  component: lazyRouteComponent(() => import('./pages/delegations-page'), 'DelegationsPage'),
});

// Accounts (ADR-0031): own sign-in security, and the owner's setup guide.
const accountRoute = createRoute({
  getParentRoute: () => backofficeRoute,
  path: 'account',
  component: lazyRouteComponent(() => import('./pages/account-page'), 'AccountPage'),
});

const onboardingRoute = createRoute({
  getParentRoute: () => backofficeRoute,
  path: 'onboarding',
  component: lazyRouteComponent(() => import('./pages/onboarding-page'), 'OnboardingPage'),
});

// Finance pages moved to /keuangan; old links and bookmarks keep working.
const movedToKeuangan = ['ap', 'ap/$', 'shifts', 'budgets', 'reconciliation'].map((path) =>
  createRoute({
    getParentRoute: () => backofficeRoute,
    path,
    beforeLoad: ({ location }) => {
      throw redirect({
        href: location.href.replace(/^\/backoffice\//, '/keuangan/'),
        replace: true,
      });
    },
  }),
);

export const backofficeTree = backofficeRoute.addChildren([
  accountRoute,
  onboardingRoute,
  ...movedToKeuangan,
  dashboardRoute,
  floorsRoute,
  tableDetailRoute,
  menuRoute,
  menuDetailRoute,
  promotionsRoute,
  pricingRoute,
  guestContactsRoute,
  qrCodesRoute,
  stationsRoute,
  staffRoute,
  auditRoute,
  reportsRoute,
  branchesRoute,
  settingsRoute,
  dataPrivacyRoute,
  subscriptionRoute,
  subscriptionInvoiceRoute,
  supportAccessRoute,
  brandingRoute,
  approvalsRoute,
  approvalPoliciesRoute,
  delegationsRoute,
]);
